import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);
  const [isTherapistOnly, setIsTherapistOnly] = useState(false);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user) {
        setCheckingOnboarding(false);
        return;
      }

      // Don't check onboarding if already on onboarding page
      if (location.pathname === '/onboarding') {
        setCheckingOnboarding(false);
        return;
      }

      // Partner-only logins (school/employer dashboards) never use the community app.
      if ((user.app_metadata as Record<string, unknown> | undefined)?.account_type === 'partner') {
        setRedirectTo('/partner');
        setCheckingOnboarding(false);
        return;
      }

      try {
        // First check if user has a therapist profile
        const { data: therapistData } = await supabase
          .from('therapist_profiles')
          .select('user_id')
          .eq('user_id', user.id)
          .single();

        // Check if user has a regular profile with the is_therapist_only flag
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('onboarding_completed, is_therapist_only')
          .eq('user_id', user.id)
          .single();

        // If user has therapist profile but NO regular profile, redirect to therapist dashboard
        // Using React Router Navigate instead of window.location.href for SPA navigation
        if (therapistData && !profileData) {
          setRedirectTo('/therapist-dashboard');
          setCheckingOnboarding(false);
          return;
        }

        // If profile doesn't exist at all, redirect to auth
        if (profileError) {
          setRedirectTo('/auth');
          setCheckingOnboarding(false);
          return;
        }

        // If user is marked as therapist-only, redirect to therapist dashboard
        if (profileData?.is_therapist_only) {
          setIsTherapistOnly(true);
          setRedirectTo('/therapist-dashboard');
          setCheckingOnboarding(false);
          return;
        }

        // Redirect to onboarding if not completed
        if (!profileData?.onboarding_completed) {
          setNeedsOnboarding(true);
        }
      } catch (error) {
        console.error('Error checking onboarding:', error);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboarding();
  }, [user, location.pathname]);

  // Return null instead of LoadingAnimation - the NativeSplashGate covers this
  if (loading || checkingOnboarding) {
    return null;
  }

  // Handle redirects via React Router Navigate (SPA navigation, no full page reload)
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
